import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import theme from 'src/theme/theme';
import { DateFormats, formatDate, isValidDate } from 'src/utils/time';
import I18next from '../../../../i18n';
import TextButton from '../../../atomsLegacy/buttons/TextButton';
import QuestionContainer from '../QuestionContainer';
import { TextQuestionContent } from '../TextQuestion/TextQuestion';
import {
  QuestionInputContainer,
  QuestionInputHalfHeight,
  QuestionSubHeading,
} from '../TextQuestion/TextQuestion.styles';
import { NotSureContainer, PickerContainer } from './DateQuestion.styles';

interface Props {
  organisation: string;
  setEventDate(date: string): void;
  date: string | null;
}

export const DateQuestion: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const invalidDate =
    typeof props.date === 'string' &&
    props.date !== '' &&
    !isValidDate(props.date);

  const [showApproximateQuestion, setShowApproximateQuestion] =
    React.useState(invalidDate);
  const [openDatePicker, setOpenDatePicker] = React.useState(false);

  const textfieldDateString =
    typeof props.date === 'string' && props.date !== null ? props.date : '';

  const locale = I18next.language
    ? I18next.language.toLocaleLowerCase()
    : 'en_gb';

  return (
    <QuestionContainer
      organisation={props.organisation}
      backLink={'submission-type'}
      nextLink={'time'}
      requireAnswer={props.date === '' && !showApproximateQuestion}
      textFieldValue={textfieldDateString}
      i18nHeader={'public.Date.header'}
      percentage={30}
      submitAnswer={() => props.setEventDate(textfieldDateString)}
    >
      {showApproximateQuestion ? (
        <TextQuestionContent
          organisation={props.organisation}
          textFieldValue={textfieldDateString}
          i18nDescription={'public.Date.approximateDescription'}
          i18nPlaceholder={'public.Date.approximatePlaceholder'}
          setQuestionData={(text) => {
            props.setEventDate(text);
          }}
        />
      ) : (
        <QuestionInputContainer>
          <QuestionInputHalfHeight>
            <QuestionSubHeading>
              {t('public.Date.description')}
            </QuestionSubHeading>

            <PickerContainer>
              <LocalizationProvider
                adapterLocale={locale}
                dateAdapter={AdapterDayjs}
              >
                <DatePicker
                  open={openDatePicker}
                  onOpen={() => setOpenDatePicker(true)}
                  onClose={() => setOpenDatePicker(false)}
                  onAccept={() => setOpenDatePicker(false)}
                  disableFuture
                  format="DD MMMM YYYY"
                  value={
                    isValidDate(props.date?.toString())
                      ? dayjs(props.date)
                      : null
                  }
                  onChange={(date: dayjs.Dayjs | null) => {
                    // Order matters: seting open state after setEventDate causes rerender before state is updated
                    setOpenDatePicker(false);
                    props.setEventDate(
                      date
                        ? formatDate(date, DateFormats.YYYY_MM_DD, false)
                        : '',
                    );
                  }}
                  localeText={{
                    clearButtonLabel: t('public.Date.clear'),
                    cancelButtonLabel: t('public.Date.cancel'),
                    okButtonLabel: t('public.Date.ok'),
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ['clear', 'cancel', 'accept'],
                    },
                    field: {
                      clearable: false,
                    },
                    textField: {
                      variant: 'standard',
                      fullWidth: true,
                      onClick: () => setOpenDatePicker(true),
                      onFocus: (event) => event.currentTarget.blur(),
                      placeholder: t('public.Date.placeholder'),
                      margin: 'normal',
                      id: 'date-picker-dialog',
                      inputProps: {
                        readOnly: true,
                        'data-testid': 'date-picker',
                      },
                      sx: {
                        '& .MuiInput-underline:before': {
                          borderBottomColor: theme.newPalette.turquoise.T300,
                        },
                        '& .MuiInput-underline:after': {
                          borderBottomColor: theme.newPalette.turquoise.T300,
                        },
                        '&:hover .MuiInput-underline:before': {
                          borderBottomColor: theme.newPalette.turquoise.T400,
                        },
                      },
                    },
                    openPickerButton: {
                      'aria-label': 'change date',
                      sx: { color: theme.newPalette.turquoise.T300 },
                    },
                  }}
                />
              </LocalizationProvider>
              <NotSureContainer isVisible={props.date === ''}>
                <TextButton
                  onClick={() => setShowApproximateQuestion(true)}
                  data-testid={'not-sure-button'}
                  label={t('Buttons.notSure')}
                  notSureButton
                />
              </NotSureContainer>
            </PickerContainer>
          </QuestionInputHalfHeight>
        </QuestionInputContainer>
      )}
    </QuestionContainer>
  );
};

export default DateQuestion;
